const PRODUCT_REVIEWS_EMPTY_STATE = {
  ok: true,
  aggregate: {
    averageRating: 0,
    totalReviews: 0,
    distribution: {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    },
    media: [],
  },
  reviews: [],
};

class ProductReviews extends HTMLElement {
  connectedCallback() {
    this.listElement = this.querySelector("[data-review-list]");
    this.statusElement = this.querySelector("[data-review-status]");
    this.mediaElement = this.querySelector("[data-review-media] .product-reviews__media-grid");
    this.totalElements = this.querySelectorAll("[data-review-total], [data-review-total-inline]");
    this.sortElement = this.querySelector("[data-review-sort]");
    this.filterButton = this.querySelector("[data-rating-filter]");
    this.writeButton = this.querySelector("[data-write-review]");
    this.modalElement = null;
    this.formElement = null;
    this.ratingButtons = [];
    this.uploadLabelElement = null;
    this.mediaUploadEnabled = this.dataset.enableMediaUpload !== "false";
    this.allowVideoUpload = this.dataset.allowVideoUpload === "true";
    this.currentRating = "all";

    this.ensureReviewForm();

    if (this.sortElement) {
      this.sortElement.addEventListener("change", () => {
        this.loadReviews();
      });
    }

    if (this.filterButton) {
      this.filterButton.addEventListener("click", () => {
        this.rotateRatingFilter();
      });
    }

    if (this.writeButton) {
      this.writeButton.addEventListener("click", () => {
        this.openReviewForm();
      });
    }

    this.loadReviews();
  }

  async loadReviews() {
    this.setLoading(true);
    const endpoint = this.dataset.endpoint || "/apps/reviews";
    const params = new URLSearchParams({
      product_id: this.dataset.productId || "",
      product_handle: this.dataset.productHandle || "",
      sort: this.sortElement ? this.sortElement.value : this.dataset.initialSort || "highest",
    });

    if (this.currentRating !== "all") {
      params.set("rating", this.currentRating);
    }

    try {
      const response = await fetch(`${endpoint}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const payload = await response.json();
      if (!payload || !payload.ok) {
        throw new Error("Reviews endpoint returned an invalid response");
      }
      this.render(payload, false);
    } catch (error) {
      this.render(PRODUCT_REVIEWS_EMPTY_STATE, true);
      this.showStatus("Customer reviews are temporarily unavailable. Please try again later.", true);
      if (window.console && typeof window.console.warn === "function") {
        window.console.warn("Product reviews could not be loaded", error);
      }
    } finally {
      this.setLoading(false);
    }
  }

  rotateRatingFilter() {
    const ratings = ["all", "5", "4", "3", "2", "1"];
    const currentIndex = ratings.indexOf(this.currentRating);
    const nextIndex = (currentIndex + 1) % ratings.length;
    this.currentRating = ratings[nextIndex];
    const label = this.currentRating === "all" ? "Filters" : `${this.currentRating} star`;
    this.filterButton.querySelector("span:last-child").textContent = label;
    this.loadReviews();
  }

  render(payload, isFallback) {
    const aggregate = payload.aggregate || PRODUCT_REVIEWS_EMPTY_STATE.aggregate;
    const reviews = Array.isArray(payload.reviews) ? payload.reviews : [];
    const filteredReviews = this.currentRating === "all"
      ? reviews
      : reviews.filter((review) => String(review.rating) === this.currentRating);
    const sortedReviews = this.sortReviews(filteredReviews);

    this.renderSummary(aggregate);
    this.renderMedia(aggregate.media || []);
    this.renderReviews(sortedReviews);
    this.updateTotals(filteredReviews.length || aggregate.totalReviews || 0);

    if (!sortedReviews.length) {
      this.listElement.innerHTML = '<div class="product-reviews__empty">No reviews matched this filter yet.</div>';
    }

    if (!isFallback) {
      this.hideStatus();
    }
  }

  renderSummary(aggregate) {
    const scoreElement = this.querySelector(".product-reviews__score");
    const noteElement = this.querySelector(".product-reviews__score-note");
    const starsElement = this.querySelector("[data-review-stars]");
    const distribution = aggregate.distribution || {};
    const totalReviews = Number(aggregate.totalReviews || 0);

    if (scoreElement) {
      scoreElement.textContent = totalReviews > 0
        ? Number(aggregate.averageRating || 0).toFixed(1)
        : "—";
    }

    if (noteElement) {
      noteElement.textContent = totalReviews > 0
        ? `Based on ${this.formatNumber(totalReviews)} published customer review${totalReviews === 1 ? "" : "s"}`
        : "No customer reviews yet";
    }

    if (starsElement) {
      starsElement.hidden = totalReviews === 0;
    }

    this.querySelectorAll("[data-rating-row]").forEach((row) => {
      const rating = row.getAttribute("data-rating-row");
      const count = Number(distribution[rating] || 0);
      const fill = row.querySelector(".product-reviews__distribution-fill");
      const countElement = row.querySelector(".product-reviews__distribution-count");
      const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;

      if (fill) {
        fill.style.width = `${percentage}%`;
      }

      if (countElement) {
        countElement.textContent = this.compactNumber(count);
      }
    });
  }

  renderMedia(mediaItems) {
    if (!this.mediaElement) return;

    this.mediaElement.innerHTML = mediaItems
      .slice(0, 7)
      .map((item) => {
        const src = this.escapeHtml(item.src || "");
        const alt = this.escapeHtml(item.alt || "Review media");
        if (item.kind === "video") {
          return `<div class="product-reviews__media-item"><video src="${src}" muted playsinline preload="metadata" aria-label="${alt}"></video></div>`;
        }
        return `<div class="product-reviews__media-item"><img src="${src}" alt="${alt}" loading="lazy"></div>`;
      })
      .join("");
  }

  renderReviews(reviews) {
    if (!this.listElement) return;

    this.listElement.innerHTML = reviews
      .map((review) => {
        const reviewRating = Math.max(0, Math.min(5, Math.round(Number(review.rating || 0))));
        const stars = `${"★".repeat(reviewRating)}${"☆".repeat(5 - reviewRating)}`;
        const verified = review.verifiedBuyer
          ? '<div class="product-reviews__verified"><span aria-hidden="true">●</span><span>Verified Buyer</span></div>'
          : "";
        const recommendation = review.recommendText
          ? `<div class="product-reviews__recommend">${this.escapeHtml(review.recommendText)}</div>`
          : "";
        const reviewThumb = review.image || this.dataset.productImage || "";
        const image = reviewThumb
          ? `<img class="product-reviews__variant-thumb" src="${this.escapeHtml(reviewThumb)}" alt="${this.escapeHtml(review.variantTitle || this.dataset.productTitle || "Reviewed product")}" loading="lazy">`
          : '<div class="product-reviews__variant-thumb"></div>';
        const mediaGallery = Array.isArray(review.media) && review.media.length
          ? `<div class="product-reviews__card-media">${review.media.map((item) => {
              const mediaSrc = this.escapeHtml(item.src || "");
              const mediaAlt = this.escapeHtml(item.alt || review.title || "Review media");
              if (item.kind === "video") {
                return `<div class="product-reviews__card-media-item"><video src="${mediaSrc}" controls preload="metadata" playsinline aria-label="${mediaAlt}"></video></div>`;
              }
              return `<div class="product-reviews__card-media-item"><img src="${mediaSrc}" alt="${mediaAlt}" loading="lazy"></div>`;
            }).join("")}</div>`
          : "";

        return `
          <article class="product-reviews__card">
            <div class="product-reviews__card-sidebar">
              <h3 class="product-reviews__author">${this.escapeHtml(review.reviewerName || "Anonymous")}</h3>
              ${verified}
              <div class="product-reviews__variant">
                ${image}
                <div>
                  <div>Reviewing</div>
                  <strong>${this.escapeHtml(review.variantTitle || this.dataset.productTitle || "Product")}</strong>
                </div>
              </div>
              ${recommendation}
            </div>
            <div class="product-reviews__card-main">
              <div class="product-reviews__card-stars" aria-label="${Number(review.rating || 0)} out of 5 stars">${stars}</div>
              <h4 class="product-reviews__card-title">${this.escapeHtml(review.title || "Untitled review")}</h4>
              <p class="product-reviews__card-content">${this.escapeHtml(review.content || "")}</p>
              ${mediaGallery}
              <div class="product-reviews__card-footer">
                <span>${this.escapeHtml(review.createdAtLabel || "")}</span>
                <span class="product-reviews__helpful">Was this helpful? <strong>${this.escapeHtml(String(review.helpfulCount || 0))}</strong></span>
              </div>
            </div>
          </article>
        `;
      })
      .join("");
  }

  ensureReviewForm() {
    if (this.modalElement) return;

    const productTitle = this.escapeHtml(this.dataset.productTitle || "Product");
    const uploadLabel = this.allowVideoUpload ? "Add Photos or Videos" : "Add Photos";
    const uploadAccept = this.allowVideoUpload ? "image/*,video/*" : "image/*";
    const uploadHelper = this.allowVideoUpload ? "Click here or drag to upload" : "Click here or drag images to upload";
    const productImage = this.dataset.productImage
      ? `<img class="product-reviews__form-product-image" src="${this.escapeHtml(this.dataset.productImage)}" alt="${productTitle}" loading="lazy">`
      : '<div class="product-reviews__form-product-image product-reviews__form-product-image--placeholder" aria-hidden="true"></div>';
    const uploadField = this.mediaUploadEnabled
      ? `
            <label class="product-reviews__upload product-reviews__field--full">
              <input name="mediaFiles" type="file" accept="${uploadAccept}" multiple>
              <span class="product-reviews__upload-icon" aria-hidden="true">▣</span>
              <span class="product-reviews__upload-copy">
                <strong>${uploadLabel}</strong>
                <span data-upload-label>${uploadHelper}</span>
              </span>
            </label>`
      : "";

    this.insertAdjacentHTML("beforeend", `
      <div class="product-reviews__modal" data-review-modal hidden>
        <div class="product-reviews__modal-backdrop" data-review-close></div>
        <div class="product-reviews__dialog" role="dialog" aria-modal="true" aria-labelledby="ProductReviewsDialogTitle-${this.dataset.sectionId || "default"}">
          <button class="product-reviews__dialog-close" type="button" aria-label="Close review form" data-review-close>×</button>
          <div class="product-reviews__dialog-header">
            <p class="product-reviews__eyebrow">Share your experience</p>
            <h3 class="product-reviews__dialog-title" id="ProductReviewsDialogTitle-${this.dataset.sectionId || "default"}">Write a review</h3>
          </div>
          <form class="product-reviews__form" data-review-form>
            <div class="product-reviews__form-product product-reviews__field--full">
              ${productImage}
              <div class="product-reviews__form-product-copy">
                <div class="product-reviews__form-product-title">${productTitle}</div>
                <div class="product-reviews__rating" role="radiogroup" aria-label="Rate this product">
                  <input type="hidden" name="rating" value="">
                  <button class="product-reviews__rating-star" type="button" data-rating-value="1" aria-label="Rate 1 star" aria-pressed="false">★</button>
                  <button class="product-reviews__rating-star" type="button" data-rating-value="2" aria-label="Rate 2 stars" aria-pressed="false">★</button>
                  <button class="product-reviews__rating-star" type="button" data-rating-value="3" aria-label="Rate 3 stars" aria-pressed="false">★</button>
                  <button class="product-reviews__rating-star" type="button" data-rating-value="4" aria-label="Rate 4 stars" aria-pressed="false">★</button>
                  <button class="product-reviews__rating-star" type="button" data-rating-value="5" aria-label="Rate 5 stars" aria-pressed="false">★</button>
                </div>
              </div>
            </div>
            <label class="product-reviews__field product-reviews__field--full">
              <span class="visually-hidden">Review title</span>
              <input name="title" type="text" required maxlength="120" placeholder="Review Title">
            </label>
            <label class="product-reviews__field product-reviews__field--full">
              <span class="visually-hidden">Review description</span>
              <textarea name="content" rows="6" required maxlength="1200" placeholder="Review Description"></textarea>
            </label>
            ${uploadField}
            <div class="product-reviews__form-profile product-reviews__field--full">Your Profile</div>
            <label class="product-reviews__field product-reviews__field--full">
              <span class="visually-hidden">Name</span>
              <input name="reviewerName" type="text" required maxlength="80" placeholder="Name">
            </label>
            <label class="product-reviews__field product-reviews__field--full">
              <span class="visually-hidden">Email address</span>
              <input name="reviewerEmail" type="email" required maxlength="120" placeholder="Email Address">
            </label>
            <p class="product-reviews__terms product-reviews__field--full">By submitting, you confirm that this review reflects your genuine experience with this product.</p>
            <div class="product-reviews__form-actions">
              <button class="product-reviews__submit" type="submit">Agree & Submit</button>
            </div>
          </form>
        </div>
      </div>
    `);

    this.modalElement = this.querySelector("[data-review-modal]");
    this.formElement = this.querySelector("[data-review-form]");
    this.ratingButtons = this.querySelectorAll("[data-rating-value]");
    this.uploadLabelElement = this.querySelector("[data-upload-label]");

    this.querySelectorAll("[data-review-close]").forEach((element) => {
      element.addEventListener("click", () => {
        this.closeReviewForm();
      });
    });

    this.ratingButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.setFormRating(Number(button.getAttribute("data-rating-value") || 5));
      });
    });

    const uploadInput = this.formElement ? this.formElement.querySelector('input[name="mediaFiles"]') : null;
    if (uploadInput) {
      uploadInput.addEventListener("change", () => {
        const count = uploadInput.files ? uploadInput.files.length : 0;
        if (!this.uploadLabelElement) return;
        this.uploadLabelElement.textContent = count
          ? `${count} file${count > 1 ? "s" : ""} selected`
          : (this.allowVideoUpload ? "Click here or drag to upload" : "Click here or drag images to upload");
      });
    }

    if (this.formElement) {
      this.formElement.addEventListener("submit", (event) => {
        event.preventDefault();
        this.submitReview();
      });

      this.setFormRating(0);
    }
  }

  openReviewForm() {
    if (!this.modalElement) return;
    this.modalElement.hidden = false;
    document.body.classList.add("product-reviews-modal-open");
    const firstInput = this.formElement ? this.formElement.querySelector('input[name="title"], textarea, input[name="reviewerName"]') : null;
    if (firstInput) {
      firstInput.focus();
    }
  }

  closeReviewForm() {
    if (!this.modalElement) return;
    this.modalElement.hidden = true;
    document.body.classList.remove("product-reviews-modal-open");
  }

  async submitReview() {
    if (!this.formElement) return;

    const submitButton = this.formElement.querySelector('[type="submit"]');
    const uploadInput = this.formElement.querySelector('input[name="mediaFiles"]');
    const formData = new FormData(this.formElement);
    let media = [];
    const payload = {
      productId: this.dataset.productId || "",
      productHandle: this.dataset.productHandle || "",
      productTitle: this.dataset.productTitle || "",
      rating: Number(formData.get("rating") || 0),
      title: String(formData.get("title") || "").trim(),
      content: String(formData.get("content") || "").trim(),
      reviewerName: String(formData.get("reviewerName") || "").trim(),
      reviewerEmail: String(formData.get("reviewerEmail") || "").trim(),
      media: [],
    };

    if (!payload.rating) {
      this.showStatus("Please select a star rating before submitting.", true);
      return;
    }

    if (!payload.reviewerName || !payload.reviewerEmail || !payload.title || !payload.content) {
      this.showStatus("Please complete every review field before submitting.", true);
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
    }

    try {
      if (uploadInput && uploadInput.files && uploadInput.files.length) {
        this.showStatus("Uploading media...");
        media = await this.uploadSelectedMedia(uploadInput.files);
      }

      const response = await fetch(this.dataset.endpoint || "/apps/reviews", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "content-type": "application/json",
        },
        body: JSON.stringify({
          ...payload,
          media,
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      this.formElement.reset();
      this.setFormRating(0);
      if (this.uploadLabelElement) {
        this.uploadLabelElement.textContent = this.allowVideoUpload ? "Click here or drag to upload" : "Click here or drag images to upload";
      }
      this.closeReviewForm();
      this.showStatus("Thanks. Your review has been submitted.");
      await this.loadReviews();
    } catch (error) {
      this.showStatus("Review submission failed. Please try again.", true);
      if (window.console && typeof window.console.error === "function") {
        window.console.error("Product review submission failed", error);
      }
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  }

  setFormRating(value) {
    if (!this.formElement) return;

    const numericValue = Number(value) || 0;
    const normalizedValue = Math.max(0, Math.min(5, numericValue));
    const ratingInput = this.formElement.querySelector('input[name="rating"]');
    if (ratingInput) {
      ratingInput.value = normalizedValue ? String(normalizedValue) : "";
    }

    this.ratingButtons.forEach((button) => {
      const buttonValue = Number(button.getAttribute("data-rating-value") || 0);
      const isActive = buttonValue <= normalizedValue;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  async uploadSelectedMedia(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) {
      return [];
    }

    const formData = new FormData();
    formData.set("allowVideo", this.allowVideoUpload ? "true" : "false");
    files.slice(0, 4).forEach((file) => {
      formData.append("files", file);
    });

    const response = await fetch(`${this.dataset.endpoint || "/apps/reviews"}/upload`, {
      method: "POST",
      body: formData,
    });

    const payload = await response.json();
    if (!response.ok || !payload.ok) {
      throw new Error(payload.error || "Media upload failed");
    }

    return Array.isArray(payload.media) ? payload.media : [];
  }

  sortReviews(reviews) {
    const sortValue = this.sortElement ? this.sortElement.value : "highest";
    const items = [...reviews];

    if (sortValue === "highest") {
      return items.sort((left, right) => Number(right.rating || 0) - Number(left.rating || 0));
    }

    if (sortValue === "lowest") {
      return items.sort((left, right) => Number(left.rating || 0) - Number(right.rating || 0));
    }

    return items;
  }

  updateTotals(total) {
    this.totalElements.forEach((element) => {
      element.textContent = this.formatNumber(total);
    });
  }

  setLoading(isLoading) {
    if (!this.listElement) return;

    if (isLoading) {
      this.listElement.innerHTML = `
        <article class="product-reviews__card is-skeleton" aria-hidden="true">
          <div class="product-reviews__card-sidebar"></div>
          <div class="product-reviews__card-main"></div>
        </article>
      `;
    }
  }

  showStatus(message, isError) {
    if (!this.statusElement) return;
    this.statusElement.hidden = false;
    this.statusElement.textContent = message;
    this.statusElement.classList.toggle("is-error", Boolean(isError));
  }

  hideStatus() {
    if (!this.statusElement) return;
    this.statusElement.hidden = true;
    this.statusElement.textContent = "";
    this.statusElement.classList.remove("is-error");
  }

  compactNumber(value) {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
    }

    return String(value);
  }

  formatNumber(value) {
    return new Intl.NumberFormat("en-US").format(Number(value || 0));
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}

if (!customElements.get("product-reviews")) {
  customElements.define("product-reviews", ProductReviews);
}
